import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { auth } from "@/api/endpoints";
import Toaster from "@/components/Toaster";

// External sibling tools in the Provocative suite.
const TEMPO_URL = "https://tempo.provtools.xyz";
const FLEET_TRACKER_URL = "https://fleet.provtools.xyz";

const FUNNELS = [
  { to: "/funnels/customer", label: "Customers" },
  { to: "/funnels/hardware", label: "Hardware" },
  { to: "/funnels/financing", label: "Financing" },
];

const linkClass = (isActive: boolean) =>
  `px-3 py-1.5 rounded text-sm ${
    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
  }`;

function FunnelsMenu() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const active = location.pathname.startsWith("/funnels/");

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className={linkClass(active)} onClick={() => setOpen((v) => !v)}>
        Funnels ▾
      </button>
      {open && (
        // top-full + pt-1 (padding, not margin) keeps the hover path continuous
        // from the button into the menu so it doesn't close before a click.
        <div className="absolute left-0 top-full z-20 pt-1">
          <div className="w-40 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
            {FUNNELS.map((f) => (
              <NavLink
                key={f.to}
                to={f.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-1.5 text-sm ${
                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
                  }`
                }
              >
                {f.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, clear } = useAuthStore();
  const nav = useNavigate();

  const logout = async () => {
    try {
      await auth.logout();
    } catch {
      /* ignore */
    }
    clear();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 flex items-center h-14 gap-4">
          <span className="font-semibold text-slate-900">ProvCRM</span>
          <nav className="flex items-center gap-1 flex-1">
            <NavLink to="/dashboard" className={({ isActive }) => linkClass(isActive)}>
              Dashboard
            </NavLink>
            <FunnelsMenu />
            <NavLink to="/assembly" className={({ isActive }) => linkClass(isActive)}>
              Create Deployment
            </NavLink>
          </nav>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <a href={TEMPO_URL} target="_blank" rel="noreferrer" className="hover:text-slate-900">
              Tempo ↗
            </a>
            <a href={FLEET_TRACKER_URL} target="_blank" rel="noreferrer" className="hover:text-slate-900">
              Fleet Tracker ↗
            </a>
            <span className="text-slate-300">|</span>
            <span>
              {user?.username} · {user?.role}
            </span>
            <button onClick={logout} className="text-slate-600 hover:text-slate-900">
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6">
        <Outlet />
      </main>
      <Toaster />
    </div>
  );
}
